/**
 * Phase 0 — assert the state contract on every emit.
 *
 * Almost every defect found in ten playtests was a human noticing that two
 * fields disagreed. These make that machine-checkable, so a fix can be shown to
 * have removed a defect *class* rather than the one instance someone happened to
 * see. Three of the checks below would have caught bugs shipped in 0.1.x:
 *
 *   I1  fired on 5 of 5 fielding lock-ins and was traced to two unrecoverable stalls
 *   I3  would have caught final.winner being null on the tick final.over went true
 *   I7  catches sheet corruption, which is the exact mistake the playbook warns about
 *
 * Deviation from the plan: it specifies these server-side. I1-I3 and I6 assert on
 * fields the CLIENT derives (frozen, awaitingMyAction, actionSelector, intel), so
 * asserting them on the server would only re-check a copy. They live here, next to
 * the values they constrain. I4 ("exactly one seat has a pending action") genuinely
 * does need the server — one seat cannot see the other's pending state — and is
 * deliberately not implemented here rather than faked.
 *
 * Violations are published on the blob as `violations[]` and never throw: a
 * broken invariant must not take the game down with it.
 */

/** A Got Your Number sheet is always a permutation of these. */
const GYN_MULTISET = [1, 1, 2, 3];

const violation = (id, detail, extra = {}) => ({ id, detail, ...extra });

/**
 * @param snap  the state blob about to be published
 * @param prev  the previously published blob (for monotonic / knowledge checks)
 * @param opts.queryControl  (selector) => HTMLElement|null. Injected so this is
 *                           testable without a DOM.
 */
export function checkInvariants(snap, prev = null, opts = {}) {
  const out = [];
  if (!snap) return out;
  const queryControl =
    opts.queryControl ??
    ((sel) => (typeof document !== "undefined" ? document.querySelector(sel) : null));

  try {
    // I1 — frozen and awaitingMyAction are contradictory instructions.
    // `frozen` told agents to wait; `awaitingMyAction` told them to act. The
    // fielding phase set both, and agents that obeyed `frozen` deadlocked.
    if (snap.frozen === true && snap.awaitingMyAction === true) {
      out.push(violation("I1", "frozen and awaitingMyAction are both true", {
        expectedAction: snap.expectedAction ?? null,
      }));
    }

    // I2 — if we are demanding an action, the control for it must exist and be
    // usable. An audit reported believing it should act while finding no
    // actionable button anywhere in the DOM.
    //
    // Suppressed mid-resolution: while `phase.settling` is true (or the expected
    // action is the animation wait) the control is CORRECTLY disabled, so firing
    // here reports a non-defect. That noise buried a real I2 hit — "awaitingMyAction
    // with no actionSelector" during swap_spit — in a playtest, which is the
    // failure mode a checker can least afford.
    const settling = snap.phase?.settling === true || snap.expectedAction === "wait_animation";
    if (snap.awaitingMyAction === true && !settling) {
      const sel = snap.actionSelector;
      if (!sel) {
        out.push(violation("I2", "awaitingMyAction with no actionSelector", {
          expectedAction: snap.expectedAction ?? null,
        }));
      } else {
        const el = queryControl(sel);
        if (!el) {
          out.push(violation("I2", "actionSelector matches no element", { selector: sel }));
        } else if (el.disabled === true || el.getAttribute?.("aria-disabled") === "true") {
          out.push(violation("I2", "actionSelector resolves to a disabled control", {
            selector: sel,
          }));
        }
      }
    }

    // I3 — terminal state must be internally consistent. The playbook tells
    // agents to stop on final.over and read final.winner; publishing one without
    // the other leaves them unable to report the result.
    if (snap.expectedAction === "game_over" && snap.final?.over !== true) {
      out.push(violation("I3", "expectedAction is game_over but final.over is not true"));
    }
    if (snap.final?.over === true && snap.final?.winner == null && snap.final?.status === "complete") {
      out.push(violation("I3", "final.over is true but final.winner is null"));
    }

    // I5 — seq must strictly increase, or consumers cannot tell new from stale.
    if (prev && typeof snap.seq === "number" && typeof prev.seq === "number"
        && snap.seq <= prev.seq) {
      out.push(violation("I5", "seq did not increase", { prev: prev.seq, next: snap.seq }));
    }

    // I6 — knowledge is monotonic. A revealed chip value must never become
    // unknown again; intel.opponentPitcher used to blank every half-inning.
    const prevKnown = prev?.intel?.opponentPitcher?.known;
    const nextKnown = snap.intel?.opponentPitcher?.known;
    if (prevKnown && nextKnown) {
      for (const [chip, val] of Object.entries(prevKnown)) {
        if (nextKnown[chip] == null) {
          out.push(violation("I6", `forgot opponent chip ${chip} (was ${val})`));
        } else if (nextKnown[chip] !== val) {
          out.push(violation("I6", `opponent chip ${chip} changed ${val} -> ${nextKnown[chip]}`));
        }
      }
    }

    // I7 — every learned sheet must remain a subset of {1,1,2,3}. This is the
    // sheet-corruption check: ingesting a taken pitch's 0, or merging both
    // sides' values into one sheet, both show up here.
    for (const [pid, sheet] of Object.entries(snap.intel?.sheets || {})) {
      const pool = [...GYN_MULTISET];
      for (const [chip, val] of Object.entries(sheet || {})) {
        const i = pool.indexOf(val);
        if (i < 0) {
          out.push(violation("I7", `sheet ${pid} chip ${chip} = ${val} is not available in {1,1,2,3}`));
        } else {
          pool.splice(i, 1);
        }
      }
    }

    // I7b — the same constraint on the raw evidence, before intel derives from it.
    for (const p of snap.pitches || []) {
      if (!p?.chipsApplied) continue;
      for (const [side, valKey] of [["pitcher", "pitcherChipValue"], ["batter", "batterChipValue"]]) {
        const v = p[valKey];
        if (v == null) continue;
        if (!GYN_MULTISET.includes(v)) {
          out.push(violation("I7", `${side} chip value ${v} is not a legal GYN value`, {
            resolutionId: p.resolutionId ?? null,
          }));
        }
      }
    }
  } catch (err) {
    // A broken invariant checker must never break the game.
    out.push(violation("checker_error", String(err?.message || err)));
  }
  return out;
}
